import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "~/lib/utils";

// TODO: Use correct colors after updates in Figma
const alertVariants = cva(
  [
    "group/alert relative w-full rounded-[10px] border px-4 py-3 text-sm grid items-start",
    "grid-cols-[0_1fr] gap-y-0.5",
    "bg-white border-neutral-300 dark:bg-neutral-900 dark:border-black/10",
    "has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] has-[>svg]:gap-x-3",
    "[&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current",
  ],
  {
    variants: {
      variant: {
        default: "[&>svg]:text-neutral-950 dark:[&>svg]:text-neutral-50",
        destructive: "[&>svg]:stroke-red-500 dark:[&>svg]:stroke-red-400",
        success: "[&>svg]:stroke-green-500 dark:[&>svg]:stroke-green-400",
        warning:
          "bg-amber-50 border-amber-200 [&>svg]:text-amber-900 dark:bg-amber-500/10 dark:border-amber-500/30 dark:[&>svg]:text-amber-400",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      data-variant={variant}
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        "col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight",
        "group-data-[variant=destructive]/alert:text-red-500",
        "group-data-[variant=success]/alert:text-green-700",
        "group-data-[variant=warning]/alert:text-amber-900",
        "dark:text-neutral-50 dark:group-data-[variant=destructive]/alert:text-red-400 dark:group-data-[variant=success]/alert:text-green-400 dark:group-data-[variant=warning]/alert:text-amber-400",
        className
      )}
      {...props}
    />
  );
}

function AlertDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "text-muted-foreground col-start-2 grid justify-items-start gap-1 text-sm [&_p]:leading-relaxed",
        "group-data-[variant=destructive]/alert:text-red-500",
        "group-data-[variant=success]/alert:text-green-700",
        "group-data-[variant=warning]/alert:text-amber-800",
        "dark:text-neutral-400 dark:group-data-[variant=destructive]/alert:text-red-400 dark:group-data-[variant=success]/alert:text-green-400 dark:group-data-[variant=warning]/alert:text-amber-300",
        className
      )}
      {...props}
    />
  );
}

export { Alert, AlertTitle, AlertDescription };
